import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import cn from 'classnames';
import { isAfter } from 'date-fns';
import { capitalize } from 'lodash-es';
import { Badge, Callout, Collapse, Switch, Typography } from 'seagull';
import { ChevronDown, ChevronRight, ChevronUp } from 'seagull/icons';
import { ResponsiveModal } from '~/client-flow/components/ResponsiveModal';
import '~/components/AgreementStudioPage/TemplateVariables/TemplateVariableBlot/TemplateVariableBlot';
import { VariableInlineRenderer } from '~/components/AgreementStudioPage/TemplateVariables/VariableInlineRenderer/VariableInlineRenderer';
import ServiceDescriptionContent from '~/components/ServiceDescriptionContent/ServiceDescriptionContent';
import { useIoContext, useOverlayContext } from '~/contexts';
import { useServiceBillingDetails } from '~/hooks/reactQuery/queries/useServiceBillingDetails';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { AgreementDataModel, relationshipModel, serviceCostModel, serviceModel } from '~/models';
import { ServiceCostType } from '~/models/service-cost';
import { formatLongMonthDateWithLocalTimezone, utils } from '~/utils';
import { bulletPointsTextsPath, getServiceBulletPoints } from '~/utils/service-description/getServiceBulletPoints';
import { billingBadgesTextsPath, billingDetailsBadge, discountBadge as getDiscountBadge } from '~/utils/service-description/getServiceDescriptionBadges';
import { useResponsive } from '../../contexts';
import { OverrideBranding } from '../CobBrandingContext';
import styles from './Service.module.scss';
import { getLimitedTimeDiscountOfferCalloutTextForService } from './Service.utils';
export function Service(param) {
    let { idx, relationship, service, optionallySelected, onChangeOptionallySelected } = param;
    var _service_bundle;
    const overlayContext = useOverlayContext();
    const { isDesktop, isMobile } = useResponsive();
    const { getNow } = useIoContext();
    const { t: billingBadgesTexts } = useLocaleKeys(billingBadgesTextsPath);
    const { t: bulletPointsTexts } = useLocaleKeys(bulletPointsTextsPath);
    const { t: calloutsTexts } = useLocaleKeys('billing.callouts');
    const { t: rsTexts } = useLocaleKeys('clientFlow.reviewServices');
    const now = useMemo(()=>getNow(), []);
    const agreementData = useMemo(()=>AgreementDataModel.fromRelationship(relationship), [
        relationship
    ]);
    const [showContent, setShowContent] = useState(false);
    const isOptional = service.participation.type === 'optional';
    const featureFlags = useFeatureFlags();
    const { periods: priorPeriodsData } = useServiceBillingDetails({
        service,
        relationshipEffectiveDate: relationshipModel.effectiveDate(relationship)
    });
    const getTypographySkin = (defaultSkin)=>isOptional && !optionallySelected ? 'secondary' : defaultSkin || 'standard';
    const discountBadgeData = getDiscountBadge(service, billingBadgesTexts);
    const hasBundleDescription = useMemo(()=>{
        var _service_bundle;
        return (_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services.some((service)=>service.description);
    }, [
        (_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services
    ]);
    const bulletPoints = getServiceBulletPoints({
        service,
        now,
        agreementData,
        isReadOnly: true,
        skin: 'secondary',
        featureFlags,
        texts: bulletPointsTexts
    });
    const discountBadge = discountBadgeData ? /*#__PURE__*/ _jsx(Badge, {
        rounded: true,
        outlined: true,
        skin: discountBadgeData.skin,
        testId: "service-discount-badge-".concat(service.id),
        children: discountBadgeData.text
    }) : /*#__PURE__*/ _jsx(_Fragment, {});
    const billingBadge = /*#__PURE__*/ _jsx(Badge, {
        rounded: true,
        outlined: true,
        children: billingDetailsBadge(service, {
            t: billingBadgesTexts,
            effectiveDate: agreementData.effectiveDate,
            isClientPreview: true,
            now
        }).text
    });
    const getLimitedTimeDiscountOfferCallout = ()=>{
        const calloutText = getLimitedTimeDiscountOfferCalloutTextForService(service);
        if (!calloutText) return null;
        return /*#__PURE__*/ _jsx("div", {
            className: styles.limitedTimeDiscountOfferCalloutWrapper,
            children: /*#__PURE__*/ _jsx(Callout, {
                intent: "info",
                className: styles.limitedTimeDiscountOfferCallout,
                withBorder: true,
                testId: "limited-time-discount-offer-callout-".concat(service.id),
                children: calloutText
            })
        });
    };
    function priceComplex() {
        switch(service.cost.pricingType){
            case ServiceCostType.Fixed:
            case ServiceCostType.Variable:
                var _service_cost_discount;
                return /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h6",
                            skin: getTypographySkin(),
                            children: utils.formatDollarAmount(utils.applyDiscount(service.cost.price, service.cost.discount))
                        }),
                        ((_service_cost_discount = service.cost.discount) === null || _service_cost_discount === void 0 ? void 0 : _service_cost_discount.amount()) && /*#__PURE__*/ _jsx(Typography, {
                            skin: getTypographySkin('secondary'),
                            textDecoration: "line-through",
                            className: styles.priceAbove,
                            children: utils.formatDollarAmount(service.cost.price)
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            skin: getTypographySkin('secondary'),
                            children: serviceCostModel.isVariablePrice(service.cost) ? "/ ".concat(capitalize(serviceCostModel.formattedUnitName(service.cost))) : ''
                        })
                    ]
                });
            case ServiceCostType.Range:
                var _service_cost_discount1;
                return /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            variant: "h6",
                            skin: getTypographySkin(),
                            children: [
                                service.cost.minPrice && !service.cost.minPrice.eq(0) ? "".concat(utils.formatDollarAmount(utils.applyDiscount(service.cost.minPrice, service.cost.discount)), " - ") : rsTexts.upTo(),
                                utils.formatDollarAmount(utils.applyDiscount(service.cost.maxPrice, service.cost.discount))
                            ]
                        }),
                        ((_service_cost_discount1 = service.cost.discount) === null || _service_cost_discount1 === void 0 ? void 0 : _service_cost_discount1.amount()) && /*#__PURE__*/ _jsxs(Typography, {
                            skin: getTypographySkin('secondary'),
                            textDecoration: "line-through",
                            className: styles.priceAbove,
                            children: [
                                !!service.cost.minPrice && "".concat(utils.formatDollarAmount(service.cost.minPrice), " - "),
                                utils.formatDollarAmount(service.cost.maxPrice)
                            ]
                        })
                    ]
                });
        }
    }
    function getServiceCallout() {
        const startBillingAt = serviceModel.startBillingAt(service);
        if (!startBillingAt) return null;
        if (priorPeriodsData && priorPeriodsData > 0) {
            return /*#__PURE__*/ _jsx(Callout, {
                intent: "warning",
                testId: "billing-at-callout-".concat(service.id),
                className: styles.callout,
                children: rsTexts.priorPeriodsCallout({
                    count: priorPeriodsData
                })
            });
        }
        if (isAfter(startBillingAt, now)) {
            return /*#__PURE__*/ _jsx(Callout, {
                intent: "info",
                testId: "billing-at-callout-".concat(service.id),
                withBorder: true,
                className: styles.callout,
                children: calloutsTexts.futureBillingCallout({
                    date: formatLongMonthDateWithLocalTimezone(startBillingAt)
                })
            });
        }
    }
    function getServiceDescription() {
        const title = hasBundleDescription ? rsTexts.bundleDetails() : rsTexts.serviceDetails();
        return /*#__PURE__*/ _jsxs("div", {
            className: styles.serviceDescription,
            children: [
                (service.description || hasBundleDescription) && /*#__PURE__*/ _jsxs("section", {
                    className: styles.section,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: cn(styles.sectionTitle, {
                                [styles.mobile]: isMobile
                            }),
                            children: /*#__PURE__*/ _jsx(Typography, {
                                children: title
                            })
                        }),
                        /*#__PURE__*/ _jsx(ServiceDescriptionContent, {
                            description: service.description,
                            includedServices: service.bundle ? service.bundle.services : null,
                            testId: "agreement-terms-service-".concat(idx)
                        })
                    ]
                }),
                bulletPoints.bullets.length > 0 && /*#__PURE__*/ _jsxs("section", {
                    className: styles.section,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: cn(styles.sectionTitle, {
                                [styles.mobile]: isMobile
                            }),
                            children: /*#__PURE__*/ _jsx(Typography, {
                                children: rsTexts.billing()
                            })
                        }),
                        /*#__PURE__*/ _jsx("ul", {
                            className: styles.billingBullets,
                            "data-testid": "agreement-terms-service-".concat(idx, "-billing"),
                            children: bulletPoints.bullets.map((text, i)=>/*#__PURE__*/ _jsx("li", {
                                    className: styles.listItem,
                                    "data-testid": "agreement-terms-service-".concat(idx, "-billing-bullet-").concat(i),
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        skin: "secondary",
                                        children: text
                                    })
                                }, i))
                        })
                    ]
                })
            ]
        });
    }
    function getIncludedServicesTitle() {
        var _service_bundle;
        return (service === null || service === void 0 ? void 0 : (_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services) && service.bundle.services.length > 0 ? /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    children: rsTexts.includedServices()
                }),
                /*#__PURE__*/ _jsx("ul", {
                    className: styles.includedServices,
                    "data-testid": "agreement-terms-service-".concat(idx, "-included-services"),
                    children: service.bundle.services.map((includedService)=>/*#__PURE__*/ _jsx(Typography, {
                            skin: "secondary",
                            as: "li",
                            children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                                text: includedService.name
                            })
                        }, includedService.serviceTemplateId))
                })
            ]
        }) : null;
    }
    function openServiceModal() {
        return overlayContext.showModal({
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsxs(ResponsiveModal, {
                    backTitle: rsTexts.backToAgreement(),
                    title: service.name,
                    isDesktop: isDesktop,
                    onClose: dismissModal,
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.mobileModalPrice,
                            "data-testid": "service-modal-pricing",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: styles.mobileModalPriceInnerRow,
                                    children: priceComplex()
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: discountBadge
                                })
                            ]
                        }),
                        getServiceDescription()
                    ]
                });
            }
        });
    }
    const optionalSwitch = isOptional && /*#__PURE__*/ _jsx(Switch, {
        intent: Intent.SelectOptionalService,
        checked: optionallySelected,
        testId: "agreement-terms-service-".concat(idx, "-optional-toggle"),
        onChange: ()=>onChangeOptionallySelected && onChangeOptionallySelected(!optionallySelected)
    });
    const pausedCallout = bulletPoints.pausedCallout && /*#__PURE__*/ _jsx(Callout, {
        withBorder: true,
        className: styles.callout,
        testId: "agreement-terms-service-".concat(idx, "-paused"),
        children: bulletPoints.pausedCallout
    });
    return /*#__PURE__*/ _jsx("div", {
        className: styles.root,
        children: !isMobile ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.accordionTitle,
                    "data-testid": "agreement-terms-service-".concat(idx),
                    onClick: ()=>setShowContent(!showContent),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.nameWrapper,
                            children: [
                                optionalSwitch,
                                /*#__PURE__*/ _jsx(Typography, {
                                    className: styles.name,
                                    ellipsis: true,
                                    weight: "normal",
                                    skin: getTypographySkin(),
                                    children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                                        text: service.name
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(OverrideBranding, {
                            className: styles.badges,
                            children: [
                                discountBadge,
                                billingBadge
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.price,
                            children: priceComplex()
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.toggleIcon,
                            children: showContent ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronDown, {})
                        }),
                        getIncludedServicesTitle(),
                        pausedCallout,
                        getServiceCallout()
                    ]
                }),
                getLimitedTimeDiscountOfferCallout(),
                /*#__PURE__*/ _jsx(Collapse, {
                    in: showContent,
                    children: /*#__PURE__*/ _jsx("div", {
                        className: styles.accordionContent,
                        children: getServiceDescription()
                    })
                })
            ]
        }) : /*#__PURE__*/ _jsxs("div", {
            onClick: openServiceModal,
            className: styles.mobileButton,
            "data-testid": "agreement-terms-service-mobile-".concat(idx),
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: cn(styles.mobileHeader, styles.nameWrapper),
                            children: [
                                optionalSwitch,
                                /*#__PURE__*/ _jsx(Typography, {
                                    className: styles.mobileTitle,
                                    weight: "normal",
                                    skin: getTypographySkin(),
                                    children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                                        text: service.name
                                    })
                                })
                            ]
                        }),
                        getIncludedServicesTitle(),
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.mobileDetails,
                            "data-testid": "agreement-terms-service-pricing",
                            children: priceComplex()
                        }),
                        /*#__PURE__*/ _jsxs(OverrideBranding, {
                            className: styles.mobileBadges,
                            children: [
                                discountBadge,
                                billingBadge
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.mobileChevron,
                    children: /*#__PURE__*/ _jsx(ChevronRight, {})
                }),
                pausedCallout
            ]
        })
    });
}
